import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import { storesApi, usersApi } from '../api/endpoints';
import { queryKeys } from '../api/queryKeys';
import type { Store } from '../api/types';
import { useAuth } from '../auth/AuthContext';

/**
 * GLOBAL STORE CONTEXT (SPEC §5): «Администратор» (storeId === null) sees the
 * whole business; a selected store scopes every page and pre-fills/hides the
 * store field in entry forms. The owner (SUPERADMIN) may also pick a
 * store-restricted USER — «как видит пользователь X» — which scopes every page
 * to that user's SET of stores (`storeIds`). Persisted in localStorage,
 * validated against the live stores/users lists (a stale value resets to
 * «Администратор»).
 */

const STORAGE_KEY = 'ribaAcc.storeContext';
const USER_PREFIX = 'user:';

/** A store-restricted user the owner can «view as». */
export interface ScopeUser {
  id: string;
  name: string;
  storeIds: string[];
}

export type StoreScope =
  { kind: 'all' } | { kind: 'store'; storeId: string } | { kind: 'user'; userId: string };

interface StoreContextValue {
  scope: StoreScope;
  setScope: (scope: StoreScope) => void;
  /** null = «Администратор» (all stores) or a user scope; set only for ONE store. */
  storeId: string | null;
  setStoreId: (id: string | null) => void;
  /** The user-scope store SET; null unless a user is selected. */
  storeIds: string[] | null;
  /** Active (non-archived) stores the caller may see at all. */
  stores: Store[];
  /** Stores inside the current scope — what pickers should offer. */
  scopeStores: Store[];
  storesLoading: boolean;
  /** The selected store row, when a store is selected and already loaded. */
  currentStore: Store | null;
  /** Store-restricted users the owner may «view as» (empty for non-superadmins). */
  scopeUsers: ScopeUser[];
  currentScopeUser: ScopeUser | null;
}

const StoreContext = createContext<StoreContextValue | null>(null);

function readPersisted(): StoreScope {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return { kind: 'all' };
  if (raw.startsWith(USER_PREFIX)) return { kind: 'user', userId: raw.slice(USER_PREFIX.length) };
  return { kind: 'store', storeId: raw };
}

function persist(scope: StoreScope): void {
  if (scope.kind === 'all') localStorage.removeItem(STORAGE_KEY);
  else if (scope.kind === 'store') localStorage.setItem(STORAGE_KEY, scope.storeId);
  else localStorage.setItem(STORAGE_KEY, USER_PREFIX + scope.userId);
}

export function StoreContextProvider({ children }: { children: ReactNode }): JSX.Element {
  const { user, isSuperadmin, hasBusiness } = useAuth();
  // A SELLER is permanently locked to their own store — no free switching.
  const sellerStoreId = user?.role === 'SELLER' ? (user.storeId ?? null) : null;
  const [scope, setScopeState] = useState<StoreScope>(readPersisted);

  // No business in scope (platform admin before picking one) → nothing to list.
  const storesQuery = useQuery({
    queryKey: queryKeys.stores(false),
    queryFn: () => storesApi.list(false),
    enabled: hasBusiness,
  });
  const stores = storesQuery.data;

  // Only the owner can list users (GET /users is superadmin-only) — nobody
  // else gets the «view as» entries, and no 403 is ever fired for them.
  const usersQuery = useQuery({
    queryKey: queryKeys.users,
    queryFn: usersApi.list,
    enabled: isSuperadmin && hasBusiness,
    staleTime: 60_000,
  });
  const scopeUsers = useMemo<ScopeUser[]>(
    () =>
      (usersQuery.data ?? [])
        .filter((u) => u.isActive && u.role === 'USER' && u.storeIds.length > 0)
        .map((u) => ({ id: u.id, name: u.name, storeIds: u.storeIds })),
    [usersQuery.data],
  );

  const setScope = useCallback(
    (next: StoreScope): void => {
      if (sellerStoreId) return; // sellers cannot change their store
      setScopeState(next);
      persist(next);
    },
    [sellerStoreId],
  );
  const setStoreId = useCallback(
    (id: string | null): void => setScope(id ? { kind: 'store', storeId: id } : { kind: 'all' }),
    [setScope],
  );

  // Validation: a persisted store that no longer exists (reseed, archived…)
  // or a user that vanished / was un-restricted / deactivated → fall back to
  // «Администратор». Not for sellers — their store is fixed from the token.
  useEffect(() => {
    if (sellerStoreId) return;
    if (scope.kind === 'store' && stores && !stores.some((s) => s.id === scope.storeId)) {
      setScope({ kind: 'all' });
    }
    if (
      scope.kind === 'user' &&
      (!isSuperadmin || (usersQuery.data && !scopeUsers.some((u) => u.id === scope.userId)))
    ) {
      setScope({ kind: 'all' });
    }
  }, [scope, stores, scopeUsers, usersQuery.data, isSuperadmin, setScope, sellerStoreId]);

  const value = useMemo<StoreContextValue>(() => {
    const all = stores ?? [];
    const base = {
      setScope,
      setStoreId,
      stores: all,
      storesLoading: storesQuery.isLoading,
      scopeUsers,
    };
    if (sellerStoreId) {
      return {
        ...base,
        scope: { kind: 'store', storeId: sellerStoreId },
        storeId: sellerStoreId,
        storeIds: null,
        scopeStores: all.filter((s) => s.id === sellerStoreId),
        currentStore: all.find((s) => s.id === sellerStoreId) ?? null,
        currentScopeUser: null,
      };
    }
    if (scope.kind === 'store' && (!stores || stores.some((s) => s.id === scope.storeId))) {
      const current = all.find((s) => s.id === scope.storeId) ?? null;
      return {
        ...base,
        scope,
        storeId: scope.storeId,
        storeIds: null,
        scopeStores: current ? [current] : [],
        currentStore: current,
        currentScopeUser: null,
      };
    }
    const scopeUser =
      scope.kind === 'user' ? (scopeUsers.find((u) => u.id === scope.userId) ?? null) : null;
    if (scopeUser) {
      const ids = scopeUser.storeIds.filter((id) => all.some((s) => s.id === id));
      return {
        ...base,
        scope,
        storeId: null,
        storeIds: ids.length > 0 ? ids : scopeUser.storeIds,
        scopeStores: all.filter((s) => ids.includes(s.id)),
        currentStore: null,
        currentScopeUser: scopeUser,
      };
    }
    return {
      ...base,
      scope: { kind: 'all' },
      storeId: null,
      storeIds: null,
      scopeStores: all,
      currentStore: null,
      currentScopeUser: null,
    };
  }, [scope, setScope, setStoreId, stores, storesQuery.isLoading, sellerStoreId, scopeUsers]);

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStoreContext(): StoreContextValue {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStoreContext outside StoreContextProvider');
  return ctx;
}
